import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Col, Form, Button, Alert, Dropdown } from "react-bootstrap";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUserTie } from "react-icons/fa";
import { motion } from "framer-motion";
import logo from "../Images/AZ.png";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from 'react-toastify';
import debounce from "lodash.debounce";

const SignIn = () => {

  const [showPassword, setShowPassword] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffSearch, setStaffSearch] = useState("");
  const [logs, setLogs] = useState([]);
  const [staffLog, setStaffLog] = useState("");
  const handleChangeEmailRef = useRef(null);
  const hasFetched = useRef(false);

  const [formData, setFormData] = useState({
    staff_name: "",
    email_address: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === "email_address" && value.trim().length > 3) {
      handleChangeEmailRef.current(value);
    } else {
      setFormData(prev => ({ ...prev, customer_name: "" }));
    }
  };

  const handleSelect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const fetchCustomerData = debounce(async (email) => {
      if (!email || email.trim().length < 3) return;

      try {
        const res = await axios.get('https://www.azzenterprises.com/api/customers/logstatus', {
          params: { email_address: email }
        });

        if (res.status === 200) {
          const { logs, customer_name, staff_name } = res.data;
          setFormData(prev => ({ ...prev, customer_name: customer_name || "" }));
          setStaffLog(staff_name || "");
          setLogs(logs || []);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setFormData(prev => ({ ...prev, customer_name: "" }));
          setStaffLog("");
          setLogs([]);
        } else {
          console.error("Error fetching customer data:", err);
        }
      }
    }, 1500);

    handleChangeEmailRef.current = fetchCustomerData;

    return () => fetchCustomerData.cancel();
  }, []);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get("https://www.azzenterprises.com/api/admin/staff");
        setStaff(res.data);
      } catch (error) {
        toast.error("Failed to load staff");
      }
    };

    if (!hasFetched.current) {
      fetchStaff();
      hasFetched.current = true
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email_address || !formData.password) {
      setError('Both fields are required');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post('https://www.azzenterprises.com/api/customers/signin', {
        email_address: formData.email_address,
        password: formData.password
      }, {
        withCredentials: true
      });

      // localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('userData', JSON.stringify({
        ...response.data.user,
        userId: response.data.userId,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000)
      }));

      const now = new Date();
      const loginTimeSG = now.toLocaleString('en-US', {
        timeZone: 'Asia/Singapore',
        hour12: true
      });

      await axios.post(
        'https://www.azzenterprises.com/api/customers/logs',
        {
          staff_name: formData.staff_name,
          email_address: formData.email_address,
          login_time: loginTimeSG
        },
        { withCredentials: true }
      );

      navigate("/personal");

    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError('Invalid email or password');
        } else if (err.response.status === 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(err.response.data.error || 'Login failed');
        }
      } else if (err.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred.');
      }
      setFormData(prev => ({ ...prev, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <div className="d-flex min-vh-100">
      <Helmet>
        <title>Sign In | Dashboard</title>
      </Helmet>

      <ToastContainer />

      {/* Left Section - Graphic/Illustration */}
      <Col md={6} className="d-none d-md-flex flex-column justify-content-center align-items-center p-5"
        style={{
          background: 'linear-gradient(135deg, #221c8bff 0%,rgba(255, 89, 172, 1) 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>

        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)'
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '-80px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)'
        }}></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
          style={{ zIndex: 1, maxWidth: '500px' }}
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          >
            <img
              src={logo}
              alt="Dashboard UI"
              className="img-fluid mb-4"
              style={{
                maxWidth: "80px",
                filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))"
              }}
            />
          </motion.div>

          <motion.h2
            className="text-white mb-3 fw-bold"
            style={{ fontSize: '2rem' }}
            key={formData.customer_name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Welcome Back! {formData.customer_name}
          </motion.h2>

          <p className="text-white-50 mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            Sign in to access your personalized dashboard
          </p>

          {/* Login Logs */}
          <h5 className="text-white mb-3" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            Recent logins
          </h5>
          <div
            className="text-start mt-4 w-100 scrollable-logs"
            style={{
              maxHeight: '250px',
              overflowY: logs.length > 4 ? 'auto' : 'hidden',
              paddingRight: '5px',
            }}
          >
            {logs.length === 0 ? (
              <p className="text-white-50 text-center">No login activity yet.</p>
            ) : (
              <motion.ul
                className="list-unstyled mb-0"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: { staggerChildren: 0.1 }
                  }
                }}
              >
                {logs.slice().reverse().slice(0, 2).map((log) => (
                  <motion.li
                    key={log.id}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 rounded"
                    style={{
                      background: log.staff_name === staffLog
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(255, 255, 255, 0.1)',
                      color: log.staff_name === staffLog ? '#ffd700' : '#fff',
                      cursor: 'default',
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                  >
                    <span className="fw-bold">{log.staff_name}</span>
                    <span className="text-white-50" style={{ fontSize: '0.875rem' }}>
                      {log.login_time}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </div>
        </motion.div>
      </Col>


      {/* Right Section - Form */}
      <Col md={6} className="d-flex justify-content-center align-items-center p-4">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="w-100 bg-white rounded-4 shadow-sm p-4 p-md-5"
          style={{
            maxWidth: "500px",
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Form Header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-4"
          >
            <h3 className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '1.75rem' }}>Sign In to Your Account</h3>
            <p className="text-muted" style={{ fontSize: '1rem' }}>Enter your credentials to continue</p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>

            {/* Staff Dropdown */}
            <motion.div variants={itemVariants}>
              <Form.Group className="mb-4 position-relative">
                <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-primary">
                  <FaUserTie />
                </span>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    id="staff-dropdown"
                    className="w-100 text-start ps-5 py-3 rounded-3"
                    style={{
                      border: "1px solid #e2e8f0",
                      fontSize: "1rem",
                      transition: "all 0.3s",
                      cursor: "pointer"
                    }}
                  >
                    {formData.staff_name || "Handled By"}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="w-100 p-2 dropdown-scroll">
                    {/* Search Input */}
                    <Form.Control
                      type="text"
                      placeholder="Search staff"
                      className="mb-2"
                      onChange={(e) => setStaffSearch(e.target.value.toLowerCase())}
                      autoFocus
                    />

                    {/* Filtered staff list */}
                    {staff
                      .filter((s) => s.user_name.toLowerCase().includes(staffSearch))
                      .map((s, idx) => (
                        <Dropdown.Item
                          key={idx}
                          onClick={() => handleSelect("staff_name", s.user_name)}
                          active={formData.staff_name === s.user_name}
                        >
                          {s.user_name}
                        </Dropdown.Item>
                      ))}

                    {/* Empty state */}
                    {staff.filter((s) =>
                      s.user_name.toLowerCase().includes(staffSearch)
                    ).length === 0 && (
                        <div className="text-muted px-2 py-1">No staff found</div>
                      )}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </motion.div>

            {/* Email Field */}
            <motion.div variants={itemVariants}>
              <Form.Group className="mb-4 position-relative">
                <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-primary">
                  <FaEnvelope />
                </span>
                <Form.Control
                  type="email"
                  name="email_address"
                  value={formData.email_address}
                  onChange={handleChange}
                  placeholder="Email address"
                  className="ps-5 py-3 rounded-3"
                  style={{
                    border: "1px solid #e2e8f0",
                    fontSize: "1rem",
                    transition: "all 0.3s",
                  }}
                  required
                />
              </Form.Group>
            </motion.div>


            {/* Password Field */}
            <motion.div variants={itemVariants}>
              <Form.Group className="mb-3">
                <div className="position-relative">
                  <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-primary">
                    <FaLock />
                  </span>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Customer password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="ps-5 py-3 rounded-3"
                    style={{
                      border: '1px solid #e2e8f0',
                      fontSize: '1rem',
                      transition: 'all 0.3s'
                    }}
                    required
                  />
                  <Button
                    variant="link"
                    className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted p-0"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ background: 'none', border: 'none' }}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <Link to="/forgot-password" className="text-primary text-decoration-none" style={{ fontSize: '0.875rem' }}>
                    Forgot password?
                  </Link>
                </div>
              </Form.Group>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              <Button
                variant="primary"
                type="submit"
                className="w-100 py-3 fw-bold rounded-3 mb-3 border-0"
                style={{
                  background: 'linear-gradient(135deg, #4f46e5 0%,rgb(58, 61, 237) 100%)',
                  fontSize: '1rem',
                  transition: 'all 0.3s'
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </motion.div>

            {/* Sign Up Link */}
            <motion.div variants={itemVariants} className="text-center mt-4">
              <p className="text-muted">
                Don't have an account? <Link to="/sign-up" className="text-primary fw-bold text-decoration-none">Sign Up</Link>
              </p>
            </motion.div>
          </Form>
        </motion.div>
      </Col>
    </div>
  );
};

export default SignIn;